import { Injectable } from '@angular/core';
import { ApiService } from '../../api/api.service';
import {
  QueryableTable,
  QueryableTableAssignment,
  MutatableEntity,
  MutatableEntityAssignment,
} from '../../api/models';
import { map, Observable } from 'rxjs';
import { getAllQueryableTables as getAllQueryableTablesFn } from '../../api/fn/graph-ql/get-all-queryable-tables';
import {
  getQueryableTableAssignmentsByOrganizationIdAndAuthorizedParty as getAssignmentsFn,
  GetQueryableTableAssignmentsByOrganizationIdAndAuthorizedParty$Params,
} from '../../api/fn/graph-ql/get-queryable-table-assignments-by-organization-id-and-authorized-party';
import {
  createQueryableTableAssignments as createAssignmentsFn,
  CreateQueryableTableAssignments$Params,
} from '../../api/fn/graph-ql/create-queryable-table-assignments';
import {
  updateQueryableTableAssignments as updateAssignmentsFn,
  UpdateQueryableTableAssignments$Params,
} from '../../api/fn/graph-ql/update-queryable-table-assignments';
import {
  deleteQueryableTableAssignments as deleteAssignmentsFn,
  DeleteQueryableTableAssignments$Params,
} from '../../api/fn/graph-ql/delete-queryable-table-assignments';
import { getAllMutatableEntities as getAllMutatableEntitiesFn } from '../../api/fn/graph-ql/get-all-mutatable-entities';
import {
  getMutatableEntityAssignmentsByOrganizationIdAndAuthorizedParty as getMutatableEntityAssignmentsFn,
  GetMutatableEntityAssignmentsByOrganizationIdAndAuthorizedParty$Params,
} from '../../api/fn/graph-ql/get-mutatable-entity-assignments-by-organization-id-and-authorized-party';
import {
  createMutatableEntityAssignments as createMutatableEntityAssignmentsFn,
  CreateMutatableEntityAssignments$Params,
} from '../../api/fn/graph-ql/create-mutatable-entity-assignments';
import {
  updateMutatableEntityAssignments as updateMutatableEntityAssignmentsFn,
  UpdateMutatableEntityAssignments$Params,
} from '../../api/fn/graph-ql/update-mutatable-entity-assignments';
import {
  deleteMutatableEntityAssignments as deleteMutatableEntityAssignmentsFn,
  DeleteMutatableEntityAssignments$Params,
} from '../../api/fn/graph-ql/delete-mutatable-entity-assignments';

@Injectable({ providedIn: 'root' })
export class GraphQLService {
  constructor(private apiService: ApiService) {}

  getAllQueryableTables(): Observable<QueryableTable[]> {
    return this.apiService
      .invoke(getAllQueryableTablesFn)
      .pipe(map((data) => data.queryableTables ?? []));
  }

  getAssignments(
    organizationId: string,
    authorizedParty: string
  ): Observable<QueryableTableAssignment[]> {
    const params: GetQueryableTableAssignmentsByOrganizationIdAndAuthorizedParty$Params = {
      organizationId,
      authorizedParty,
    };
    return this.apiService
      .invoke(getAssignmentsFn, params)
      .pipe(map((data) => data.queryableTableAssignments ?? []));
  }

  createAssignments(
    organizationId: string,
    authorizedParty: string,
    queryableTableIds: string[]
  ): Observable<QueryableTableAssignment[]> {
    const params: CreateQueryableTableAssignments$Params = {
      organizationId,
      authorizedParty,
      body: { queryableTableIds },
    };
    return this.apiService
      .invoke(createAssignmentsFn, params)
      .pipe(map((data) => data.queryableTableAssignments ?? []));
  }

  updateAssignments(
    organizationId: string,
    authorizedParty: string,
    queryableTableIds: string[]
  ): Observable<QueryableTableAssignment[]> {
    const params: UpdateQueryableTableAssignments$Params = {
      organizationId,
      authorizedParty,
      body: { queryableTableIds },
    };
    return this.apiService
      .invoke(updateAssignmentsFn, params)
      .pipe(map((data) => data.queryableTableAssignments ?? []));
  }

  deleteAssignments(
    organizationId: string,
    authorizedParty: string
  ): Observable<void> {
    const params: DeleteQueryableTableAssignments$Params = {
      organizationId,
      authorizedParty,
    };
    return this.apiService.invoke(deleteAssignmentsFn, params);
  }

  getAllMutatableEntities(): Observable<MutatableEntity[]> {
    return this.apiService
      .invoke(getAllMutatableEntitiesFn)
      .pipe(map((data) => data.mutatableEntities ?? []));
  }

  getMutatableEntityAssignments(
    organizationId: string,
    authorizedParty: string
  ): Observable<MutatableEntityAssignment[]> {
    const params: GetMutatableEntityAssignmentsByOrganizationIdAndAuthorizedParty$Params = {
      organizationId,
      authorizedParty,
    };
    return this.apiService
      .invoke(getMutatableEntityAssignmentsFn, params)
      .pipe(map((data) => data.mutatableEntityAssignments ?? []));
  }

  createMutatableEntityAssignments(
    organizationId: string,
    authorizedParty: string,
    mutatableEntityIds: string[]
  ): Observable<MutatableEntityAssignment[]> {
    const params: CreateMutatableEntityAssignments$Params = {
      organizationId,
      authorizedParty,
      body: { mutatableEntityIds },
    };
    return this.apiService
      .invoke(createMutatableEntityAssignmentsFn, params)
      .pipe(map((data) => data.mutatableEntityAssignments ?? []));
  }

  updateMutatableEntityAssignments(
    organizationId: string,
    authorizedParty: string,
    mutatableEntityIds: string[]
  ): Observable<MutatableEntityAssignment[]> {
    const params: UpdateMutatableEntityAssignments$Params = {
      organizationId,
      authorizedParty,
      body: { mutatableEntityIds },
    };
    return this.apiService
      .invoke(updateMutatableEntityAssignmentsFn, params)
      .pipe(map((data) => data.mutatableEntityAssignments ?? []));
  }

  deleteMutatableEntityAssignments(
    organizationId: string,
    authorizedParty: string
  ): Observable<void> {
    const params: DeleteMutatableEntityAssignments$Params = {
      organizationId,
      authorizedParty,
    };
    return this.apiService.invoke(deleteMutatableEntityAssignmentsFn, params);
  }
}
